// Numbers as contracts write them: "60", "sixty", "sixty (60)", "11 (Eleven)", "one lakh fifty
// thousand", "twenty-fifth", and durations ("two (2) years", "one month's") turned into months or days.

const UNITS: Record<string, number> = {
  zero: 0, nil: 0, one: 1, a: 1, an: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19,
  twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90,
};
const ORDINALS: Record<string, number> = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8, ninth: 9, tenth: 10, eleventh: 11,
  twelfth: 12, thirteenth: 13, fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17, eighteenth: 18, nineteenth: 19,
  twentieth: 20, thirtieth: 30,
};
const SCALES: Record<string, number> = { hundred: 100, thousand: 1_000, lakh: 100_000, lakhs: 100_000, lac: 100_000, lacs: 100_000, million: 1_000_000, crore: 10_000_000, crores: 10_000_000 };

/** "forty-five" → 45, "one lakh fifty thousand" → 150000, "twenty-fifth" → 25, "60" → 60. Null if it isn't a number. */
export function parseNumberWords(input: string): number | null {
  const s = input.trim().toLowerCase();
  if (/^\d+(?:\.\d+)?$/.test(s)) return Number(s);
  const words = s.replace(/-/g, " ").split(/\s+/).filter((w) => w && w !== "and");
  if (words.length === 0) return null;
  let total = 0;
  let current = 0;
  let any = false;
  for (const w of words) {
    if (w in UNITS && !(words.length === 1 && (w === "a" || w === "an"))) current += UNITS[w]!;
    else if (w in ORDINALS) current += ORDINALS[w]!;
    else if (w === "hundred") current = (current || 1) * 100;
    else if (w in SCALES) {
      total += (current || 1) * SCALES[w]!;
      current = 0;
    } else return null;
    any = true;
  }
  return any ? total + current : null;
}

const WORD = "(?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred)";
/** A count written in words ("thirty six", "twenty-four"). */
export const NUM_WORDS = `(?:${WORD}(?:[\\s-]+(?:and\\s+)?${WORD})*)`;
/** A count as digits, words or both ("sixty (60)", "11 (Eleven)", "one", "1.5"). */
export const QTY = `(?:(?:\\d{1,4}(?:\\.\\d{1,2})?|${NUM_WORDS}|an?)(?:\\s*\\(\\s*(?:\\d{1,4}|${NUM_WORDS})\\s*\\))?)`;

/** The value of a QTY match: the digits if any are written, else the words. */
export function quantity(text: string): number | null {
  const digits = /\d{1,4}(?:\.\d{1,2})?/.exec(text);
  if (digits) return Number(digits[0]);
  return parseNumberWords(text.replace(/\(.*$/, "").trim());
}

export type Unit = "day" | "week" | "month" | "year" | "hour";
export const UNIT = "(?:business\\s+|working\\s+|calendar\\s+|clear\\s+)?(day|week|month|year|hour)s?(?:['’]s?)?";
/** QTY then UNIT, e.g. "thirty (30) days'", "one month's", "2 years". Groups: 1 = quantity, 2 = unit. */
export const DURATION = `(${QTY})\\s*[-\\s]?\\s*${UNIT}`;

export const unitOf = (u: string): Unit => u.toLowerCase().replace(/s$/, "") as Unit;

/** Days in a duration (a month is 30 days, as notice clauses mean it). */
export function toDays(n: number, unit: Unit): number {
  return unit === "year" ? n * 365 : unit === "month" ? n * 30 : unit === "week" ? n * 7 : unit === "hour" ? n / 24 : n;
}

/** Months in a duration, or null when it isn't a whole number of months. */
export function toMonths(n: number, unit: Unit): number | null {
  if (unit === "year") return n * 12;
  if (unit === "month") return Number.isInteger(n) ? n : null;
  return null;
}

/** "18,00,000" / "1,250.50" / "12.000,00" → 1800000 / 1250.5 / 12000. Null if it isn't a plain number. */
export function parseAmount(s: string): number | null {
  const t = s.trim().replace(/\/-$/, "");
  if (/^\d{1,3}(?:\.\d{3})+,\d{1,2}$/.test(t)) return Number(t.replace(/\./g, "").replace(",", "."));
  if (!/^\d[\d,]*(?:\.\d+)?$/.test(t)) return null;
  const n = Number(t.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** 25000 → "25000", 99.9 → "99.9": how term values are stored. */
export const plain = (n: number) => (Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2))));
